import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PoNotificationService, PoToasterOrientation } from '@po-ui/ng-components';
import { assert, LoaderService } from 'ats-ui-lib';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';
import { AssessmentService } from '../../root/assessment/assessment.service';

@Component({
  selector: 'ats-skillcert-assessment-answer-redirect',
  imports: [],
  templateUrl: './skillcert-assessment-answer-redirect.component.html',
  styleUrl: './skillcert-assessment-answer-redirect.component.css',
})
export class SkillCertAssessmentAnswerRedirectComponent implements OnInit, OnDestroy {
  private onDestroySubject = new Subject<void>();

  @Output() finishedRedirection = new EventEmitter<void>();

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private window: Window,
    private translateService: TranslateService,
    private toasterService: PoNotificationService,
    private loaderService: LoaderService,
    private assessmentService: AssessmentService,
  ) {}

  ngOnDestroy(): void {
    this.onDestroySubject.next();
  }

  ngOnInit(): void {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(this.onRouteChange.bind(this));
  }

  private async onRouteChange(paramMap: ParamMap): Promise<void> {
    try {
      this.loaderService.show();
      const id = paramMap.get('id');
      if (id == null) {
        await this.redirectOnError('l-assessment-not-found');
        return;
      }
      const result = await lastValueFrom(this.assessmentService.getAnswerLink(id));
      if (result == null) {
        await this.redirectOnError('l-assessment-not-found');
        return;
      }
      if (!result.isAuthorized) {
        await this.redirectOnError('l-not-authorized');
        return;
      }
      assert(result.answerLink);
      this.window.location.href = result.answerLink;
    } catch (e) {
      this.loaderService.hide();
      throw e;
    } finally {
      this.finishedRedirection.emit();
    }
  }

  private redirectOnError(message: string): Promise<boolean> {
    this.loaderService.hide();
    const translatedMessage = this.translateService.instant(message);
    this.toasterService.warning({
      message: translatedMessage,
      orientation: PoToasterOrientation.Top,
    });
    return this.router.navigate(['main', 'home']);
  }
}
